/**
 * RESPONSIBILITY: stop a render error from white-screening the site, and report it.
 *
 * React unmounts the whole tree when a render throws. Without a boundary, one
 * undefined property blanks the page and the only evidence is a console line in
 * somebody else's browser.
 *
 * A class component because `componentDidCatch` has no hook equivalent — this is
 * the one place React still requires one.
 */
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { ClientErrorKind } from '@framework/core';
import { reportClientError } from './report';

interface Props {
  children: ReactNode;
}

interface State {
  message: string | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { message: null };

  static getDerivedStateFromError(error: Error): State {
    return { message: error.message || 'Something broke.' };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // The component stack is the part a minified JS stack cannot give you: which
    // component, not which byte offset.
    reportClientError(error, ClientErrorKind.Render, {
      componentStack: info.componentStack ?? undefined,
    });
  }

  render(): ReactNode {
    const { message } = this.state;
    if (message === null) return this.props.children;

    return (
      <div className="container">
        <h1>Something broke on this page</h1>
        <p className="ctl-error">{message}</p>
        <p className="ctl-hint">
          The details have been written to the server log. Reloading may clear it.
        </p>
        <button onClick={() => window.location.reload()}>Reload</button>
      </div>
    );
  }
}
